import { useState, useEffect, useMemo } from "react";
import { Image, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GalleryPage = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(null);

  // Empty sample images - gallery is managed through admin dashboard
  const sampleImages = useMemo(() => [], []);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        if (response.data.length > 0) {
          setImages(response.data);
        } else {
          setImages(sampleImages);
        }
      } catch (error) {
        console.error("Error fetching gallery:", error);
        setImages(sampleImages);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, [sampleImages]);

  const openLightbox = (index) => {
    setSelectedIndex(index);
  };

  const closeLightbox = () => {
    setSelectedIndex(null);
  };

  const goToPrevious = () => {
    setSelectedIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setSelectedIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const categories = [...new Set(images.map(img => img.category))];
  const [activeCategory, setActiveCategory] = useState("All");

  const filteredImages = activeCategory === "All" 
    ? images 
    : images.filter(img => img.category === activeCategory);

  return (
    <div className="pt-20" data-testid="gallery-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-teal-700 font-medium tracking-widest uppercase text-sm mb-4">
              Photo Gallery
            </p>
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-slate-900 leading-[1.1] mb-6">
              Moments of <span className="text-amber-700 italic">Impact</span>
            </h1>
            <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
              Explore our collection of photos from events, community outreach, legislative advocacy, and more.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => setActiveCategory("All")}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === "All"
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid="filter-all"
            >
              All
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === category
                    ? "bg-slate-900 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid={`filter-${category.toLowerCase()}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-slate-50" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : filteredImages.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Image className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No photos in this category yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredImages.map((image, index) => (
                <div
                  key={image.id}
                  className="group relative aspect-square overflow-hidden rounded-sm cursor-pointer bg-slate-200"
                  onClick={() => openLightbox(images.indexOf(image))}
                  data-testid={`gallery-image-${image.id}`}
                >
                  <img
                    src={image.url}
                    alt={image.caption}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <p className="text-white text-sm font-medium">{image.caption}</p>
                      <p className="text-slate-300 text-xs">{image.category}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <Dialog open={selectedIndex !== null} onOpenChange={closeLightbox}>
        <DialogContent className="max-w-5xl p-0 bg-black/95 border-none">
          {selectedIndex !== null && (
            <div className="relative">
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                data-testid="lightbox-close"
              >
                <X className="w-6 h-6 text-white" />
              </button>
              
              <button
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                data-testid="lightbox-next"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>

              <img
                src={images[selectedIndex]?.url}
                alt={images[selectedIndex]?.caption}
                className="w-full max-h-[80vh] object-contain"
              />
              
              <div className="p-6 text-center">
                <p className="text-white text-lg font-medium">
                  {images[selectedIndex]?.caption}
                </p>
                <p className="text-slate-400 text-sm mt-1">
                  {images[selectedIndex]?.category} • {selectedIndex + 1} of {images.length}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CTA Section */}
      <section className="py-16 bg-amber-700">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl font-bold text-white mb-4">
            Have Photos to Share?
          </h2>
          <p className="text-amber-100 mb-6">
            If you have photos from our events that you'd like to share, we'd love to feature them!
          </p>
          <a
            href="mailto:care@sicklecellhouston.org"
            className="inline-block bg-white text-amber-700 hover:bg-amber-50 font-bold uppercase tracking-wide text-sm px-8 py-3 rounded transition-colors"
          >
            Submit Your Photos
          </a>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;
